import { inject, Injectable } from '@angular/core';
import {
  FunctionBlockMessage,
  NetworkDeviceActivationMessage,
  NetworkDeviceActivationMessageEvent,
  NetworkDeviceMessage,
  NetworkDeviceMessageEvent,
} from '@ubie/mqttcommunication';
import { filter, map, Observable } from 'rxjs';
import { CommunicationListenerService } from '../communication-listen/communication-listen.service';

export enum MessageTypes {
  DashboardPopup = 'DASHBOARD_POPUP',
  CtdActivation = 'CTD_ACTIVATION',
}

export enum IpcamMessageTypes {
  RateTooHigh = 'IPCAM_RATE_TOO_HIGH',
  AuthFailure = 'IPCAM_AUTH_FAILURE',
  TunnelFailed = 'IPCAM_TUNNEL_FAILED',
}

function parseDashboardMessage(raw: string): {
  title?: string;
  message: string;
} {
  const titleMatch = raw.match(/\[TITLE\](.*?)\[MESSAGE\]/);
  const messageMatch = raw.match(/\[MESSAGE\](.*)/s);

  return {
    title: titleMatch ? titleMatch[1].trim() : undefined,
    message: messageMatch ? messageMatch[1].trim() : raw,
  };
}

@Injectable({
  providedIn: 'root',
})
export class CommunicationMessagesService {
  private readonly communicationListenerService: CommunicationListenerService =
    inject(CommunicationListenerService);

  /**
   * @description
   * Returns an observable that emits dashboard popup messages.
   * The observable is filtered by the message id being equal to
   * MessageTypes.DashboardPopup.
   * The messages are then mapped to the NetworkDeviceMessage.
   * @returns An observable of NetworkDeviceMessage
   */
  getDashboardMessages$(): Observable<
    NetworkDeviceMessage & { title?: string }
  > {
    return this.communicationListenerService
      .getNetworkDeviceMessageListener()
      .pipe(
        filter(
          (message: NetworkDeviceMessageEvent) =>
            message.message.id === MessageTypes.DashboardPopup,
        ),
        map((message: NetworkDeviceMessageEvent) => {
          const parsed = parseDashboardMessage(message.message.message);
          return {
            ...message.message,
            title: parsed.title,
            message: parsed.message,
          };
        }),
      );
  }

  getIpCamMessages$(
    ubieId: string,
    networkDeviceId: string,
    functionBlockId: string,
  ): Observable<FunctionBlockMessage & { title?: string }> {
    return this.communicationListenerService
      .getFunctionBlockMessageListener(ubieId, networkDeviceId, functionBlockId)
      .pipe(
        filter((message: FunctionBlockMessage) => {
          return [
            IpcamMessageTypes.RateTooHigh,
            IpcamMessageTypes.AuthFailure,
            IpcamMessageTypes.TunnelFailed,
          ].includes(message.id as IpcamMessageTypes);
        }),
        map((message: FunctionBlockMessage) => {
          const parsed = parseDashboardMessage(message.message);
          return {
            ...message,
            title: parsed.title,
            message: parsed.message,
          };
        }),
      );
  }

  getDashboardMessageTitle$(): Observable<string | undefined> {
    return this.getDashboardMessages$().pipe(map((message) => message.title));
  }

  /**
   * @description
   * This function returns an observable that emits ctd activation messages.
   * The observable is filtered by the device serial and network device serial.
   * The messages are then mapped to the NetworkDeviceActivationMessage.
   *
   * @param deviceSerial The serial of the device
   * @param networkDeviceSerial The serial of the network device
   * @returns An observable of NetworkDeviceActivationMessage
   */
  getCtdActivationMessages$(
    deviceSerial: string,
    networkDeviceSerial: string,
  ): Observable<NetworkDeviceActivationMessage> {
    return this.communicationListenerService
      .getNetworkDeviceActivationMessageListener()
      .pipe(
        filter(
          (message: NetworkDeviceActivationMessageEvent) =>
            message.ubieId === deviceSerial &&
            message.networkDeviceId === networkDeviceSerial &&
            message.activationMessage.id.startsWith(MessageTypes.CtdActivation),
        ),
        map(
          (message: NetworkDeviceActivationMessageEvent) =>
            message.activationMessage,
        ),
      );
  }
}
